import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import api from '../services/api';
import { listTenants } from '../services/tenantService';
import { setTenant } from '../store/slices/tenantSlice';
import { selectIsAuthenticated, selectIsSuperAdmin, selectUser } from '../store/slices/authSlice';

const SESSION_KILL_CODES = new Set([
  'TENANT_SUSPENDED',
  'TENANT_DEACTIVATED',
  'TENANT_INACTIVE',
  'DEMO_EXPIRED',
  'DEMO_LOCKED',
]);

function isTenantSessionKill(err: unknown): boolean {
  if (!(err instanceof AxiosError)) return false;
  const data = err.response?.data as { code?: string; message?: string } | undefined;
  if (data?.code && SESSION_KILL_CODES.has(data.code)) return true;
  const message = String(data?.message || '').toLowerCase();
  return message.includes('tenant is suspended')
    || message.includes('tenant is deactivated')
    || message.includes('tenant not found or inactive');
}

/**
 * Pick the client tenant for super-admin context.
 * Prefer a previously selected slug when still valid; otherwise the first listed tenant.
 * Platform `demo` is never returned.
 */
export async function resolveSuperAdminTenantSlug(opts?: {
  preferStored?: boolean;
  forceFirst?: boolean;
}): Promise<{ slug: string; companyName: string } | null> {
  const preferStored = opts?.preferStored !== false;
  const forceFirst = opts?.forceFirst === true;
  try {
    const res = await listTenants({ limit: 200, page: 1, status: 'active' });
    const rows = (res.data || []).filter((t) => t.slug && t.slug !== 'demo');
    if (!rows.length) return null;

    if (!forceFirst && preferStored) {
      const stored = (localStorage.getItem('tenantSlug') || '').trim().toLowerCase();
      if (stored && stored !== 'demo') {
        const match = rows.find((t) => t.slug === stored);
        if (match) {
          return { slug: match.slug, companyName: match.companyName || match.slug };
        }
      }
    }

    // listTenants is newest-first; reverse so the earliest created client is default.
    const ordered = [...rows].reverse();
    const first = ordered[0];
    return { slug: first.slug, companyName: first.companyName || first.slug };
  } catch {
    return null;
  }
}

/** Reload current tenant modules/features into Redux (e.g. after admin toggles modules). */
export async function refreshTenantSession(dispatch: Dispatch<UnknownAction>) {
  let slug = (localStorage.getItem('tenantSlug') || '').trim().toLowerCase();

  // Super-admin: never stay on platform demo — resolve first/valid client tenant.
  if (!slug || slug === 'demo') {
    const picked = await resolveSuperAdminTenantSlug({ preferStored: true });
    if (picked) {
      slug = picked.slug;
      localStorage.setItem('tenantSlug', slug);
    }
  }

  if (!slug) {
    dispatch(setTenant({
      slug: '',
      companyName: '',
      enabledFeatures: [],
      isDemo: false,
      demoExpiresAt: null,
    }));
    return;
  }

  try {
    const [tenantRes, modulesRes] = await Promise.all([
      api.get('/tenants/current'),
      api.get('/modules'),
    ]);
    dispatch(setTenant({
      id: tenantRes.data.data._id,
      companyName: tenantRes.data.data.companyName,
      slug: tenantRes.data.data.slug,
      branding: tenantRes.data.data.branding,
      modules: tenantRes.data.data.modules,
      currency: tenantRes.data.data.currency,
      enabledFeatures: modulesRes.data.data.enabledFeatures || [],
      isDemo: Boolean(tenantRes.data.data.isDemo),
      demoExpiresAt: tenantRes.data.data.demoExpiresAt || null,
    }));
  } catch (err) {
    if (isTenantSessionKill(err)) throw err;

    // Current slug may be invalid — fall back to first client tenant.
    const picked = await resolveSuperAdminTenantSlug({ forceFirst: true, preferStored: false });
    if (picked && picked.slug !== slug) {
      localStorage.setItem('tenantSlug', picked.slug);
      try {
        const [tenantRes, modulesRes] = await Promise.all([
          api.get('/tenants/current'),
          api.get('/modules'),
        ]);
        dispatch(setTenant({
          id: tenantRes.data.data._id,
          companyName: tenantRes.data.data.companyName,
          slug: tenantRes.data.data.slug,
          branding: tenantRes.data.data.branding,
          modules: tenantRes.data.data.modules,
          currency: tenantRes.data.data.currency,
          enabledFeatures: modulesRes.data.data.enabledFeatures || [],
          isDemo: Boolean(tenantRes.data.data.isDemo),
          demoExpiresAt: tenantRes.data.data.demoExpiresAt || null,
        }));
        return;
      } catch {
        /* fall through */
      }
    }

    dispatch(setTenant({
      slug: picked?.slug || slug,
      companyName: picked?.companyName || slug,
      enabledFeatures: [],
      isDemo: false,
      demoExpiresAt: null,
    }));
  }
}

export function useTenantBootstrap() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const user = useSelector(selectUser);

  useEffect(() => {
    if (!isAuthenticated) return;

    const load = async () => {
      if (isSuperAdmin) {
        const stored = (localStorage.getItem('tenantSlug') || '').trim().toLowerCase();
        if (!stored || stored === 'demo') {
          const picked = await resolveSuperAdminTenantSlug({ forceFirst: true, preferStored: false });
          if (picked) localStorage.setItem('tenantSlug', picked.slug);
        }
      }
      await refreshTenantSession(dispatch);
    };
    void load();

    const onFocus = () => { void refreshTenantSession(dispatch); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshTenantSession(dispatch);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [dispatch, isAuthenticated, isSuperAdmin, user?.userType]);
}
